import React, { useEffect, useState } from 'react';
import { X, Loader2, Crosshair, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { competitorsAPI } from '../services/api';
import { fmtEur } from './CompetitorPositionBadge';

const AlignPriceDialog = ({ productId, productName, onClose, onAligned }) => {
  const [offset, setOffset] = useState('0.10');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const off = parseFloat(offset.replace(',', '.'));
    if (Number.isNaN(off) || off < 0) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      competitorsAPI.alignPreview(productId, off)
        .then(p => { if (!cancelled) { setPreview(p); setError(''); } })
        .catch(e => { if (!cancelled) setError(e.response?.data?.detail || 'Aperçu impossible'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [productId, offset]);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await competitorsAPI.align(productId, parseFloat(offset.replace(',', '.')));
      toast.success(`Prix aligné : ${fmtEur(r.current_price)} → ${fmtEur(r.new_price)}`);
      onAligned?.(r);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Alignement impossible');
    } finally {
      setApplying(false);
    }
  };

  const same = preview && Math.abs(preview.new_price - preview.current_price) < 0.005;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="align-price-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2"><Crosshair size={18} className="text-orange-500" /> Aligner mon prix</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[320px]">{productName}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid="align-price-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <label className="block text-sm">
            <span className="text-gray-700">Écart sous le concurrent le moins cher (€)</span>
            <Input type="number" step="0.01" min="0" value={offset} onChange={(e) => setOffset(e.target.value)} data-testid="align-price-offset" className="mt-1 w-32" />
          </label>
          {error ? (
            <p className="text-sm text-red-600 flex items-center gap-2" data-testid="align-price-error"><AlertTriangle size={16} /> {error}</p>
          ) : loading && !preview ? (
            <div className="flex justify-center py-4"><Loader2 className="animate-spin text-orange-500" /></div>
          ) : preview && (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[11px] text-gray-500">Notre prix actuel</p><p className="font-semibold text-gray-900" data-testid="align-price-current">{fmtEur(preview.current_price)}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[11px] text-gray-500">Moins cher (C{preview.cheapest_slot})</p><p className="font-semibold text-gray-900" data-testid="align-price-cheapest">{fmtEur(preview.cheapest_price)}</p><p className="text-[10px] text-gray-400 truncate">{preview.cheapest_host}</p></div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-200"><p className="text-[11px] text-green-700">Nouveau prix</p><p className="font-bold text-green-800 text-lg" data-testid="align-price-new">{fmtEur(preview.new_price)}</p></div>
              </div>
              <p className="text-xs text-gray-500" data-testid="align-price-margin">
                {preview.margin_pct != null ? <>Marge HT après alignement : <strong className={preview.margin_pct < 15 ? 'text-red-600' : preview.margin_pct < 30 ? 'text-amber-700' : 'text-green-700'}>{preview.margin_pct} %</strong> (PA {fmtEur(preview.purchase_price)}){preview.suggested_price ? <> · prix conseillé {fmtEur(preview.suggested_price)}</> : null}</> : 'Aucun prix d\'achat fournisseur renseigné : marge non calculable.'}
              </p>
              {preview.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="align-price-warnings">
                  <p className="font-medium flex items-center gap-1.5 mb-1"><AlertTriangle size={14} /> Attention</p>
                  <ul className="list-disc ml-5 space-y-0.5 text-xs">{preview.warnings.map(w => <li key={w}>{w}</li>)}</ul>
                </div>
              )}
              {same && <p className="text-xs text-gray-500">Le prix est déjà à ce niveau.</p>}
              <p className="text-[11px] text-gray-400">Le prix barré et la remise seront retirés. Le changement est tracé dans l'historique des prix (source « veille tarifaire »).</p>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button variant="outline" onClick={onClose} data-testid="align-price-cancel">Annuler</Button>
          <Button onClick={apply} disabled={!preview || !!error || same || applying || loading} className="bg-green-600 hover:bg-green-700" data-testid="align-price-confirm">
            {applying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Appliquer {preview && !same ? fmtEur(preview.new_price) : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlignPriceDialog;
